"use client";
import { useEffect, useState } from "react";

import { useRouter, useSearchParams } from "next/navigation";

import { Button } from "@heroui/button";
import { Card, CardBody } from "@heroui/card";
import { MapPin } from "lucide-react";

import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";
import { routes } from "@/lib/routes";
import { OrganizationTerminology } from "@/lib/services/terminologyService";

interface AutoAreaFilterProps {
  orgSlug: string;
  availableAreaIds: string[];
  areaNames?: Record<string, string>; // Map of area IDs to names
  terminology: OrganizationTerminology;
}

const FILTER_KEYS = ["areas", "q", "urgency", "category"] as const;

function hasExistingFilters(
  searchParams: ReturnType<typeof useSearchParams>,
): boolean {
  if (!searchParams) return false;

  return FILTER_KEYS.some((key) => Boolean(searchParams.get(key)));
}

function getValidPreferredAreas(availableAreaIds: string[]): string[] {
  try {
    const json = localStorage.getItem("preferredAreas");

    if (!json) return [];
    const parsed: string[] = JSON.parse(json);

    return parsed.filter((id) => availableAreaIds.includes(id));
  } catch {
    console.error("Failed to load areas");

    return [];
  }
}

function applyPreferredAreaFilter({
  availableAreaIds,
  orgSlug,
  router,
  searchParams,
  setAppliedPreferredAreas,
  setShowNotification,
}: {
  availableAreaIds: string[];
  orgSlug: string;
  router: ReturnType<typeof useRouter>;
  searchParams: ReturnType<typeof useSearchParams>;
  setAppliedPreferredAreas: (areas: string[]) => void;
  setShowNotification: (show: boolean) => void;
}) {
  if (hasExistingFilters(searchParams)) return;

  const validAreas = getValidPreferredAreas(availableAreaIds);

  if (validAreas.length === 0) return;

  const params = new URLSearchParams();

  params.set("areas", validAreas.join(","));
  setAppliedPreferredAreas(validAreas);
  setShowNotification(true);
  void EventPublisher.publishEvent(EventKeys.AUTO_AREA_FILTER_APPLY, {
    areaIds: validAreas,
    org: orgSlug,
  });
  router.replace(`${routes.org.needs(orgSlug)}?${params.toString()}`);
}

/**
 * Component that automatically applies area filters based on user's preferred areas
 * stored in localStorage. Only applies on initial page load if no filters are already active.
 */
export function AutoAreaFilter({
  areaNames,
  availableAreaIds,
  orgSlug,
  terminology,
}: AutoAreaFilterProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [appliedPreferredAreas, setAppliedPreferredAreas] = useState<string[]>(
    [],
  );
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    applyPreferredAreaFilter({
      availableAreaIds,
      orgSlug,
      router,
      searchParams,
      setAppliedPreferredAreas,
      setShowNotification,
    });
  }, [orgSlug, availableAreaIds, router, searchParams]);

  const clearPreferredAreas = () => {
    localStorage.removeItem("preferredAreas");
    router.push(routes.org.needs(orgSlug));
    setShowNotification(false);
    void EventPublisher.publishEvent(EventKeys.AUTO_AREA_FILTER_CLEAR, {
      org: orgSlug,
    });
  };

  // Show notification if preferred areas were applied
  if (showNotification && appliedPreferredAreas.length > 0) {
    const areaDisplayNames = appliedPreferredAreas
      .map((id) => areaNames?.[id] ?? id)
      .join(", ");

    return (
      <Card
        className="mb-4 border-primary-200 bg-primary-50"
        data-testid="auto-filter-notification"
      >
        <CardBody className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary-600" />
              <span className="text-sm text-primary-800">
                Showing {terminology.need.plural.toLowerCase()} filtered by your
                preferred {terminology.area.plural.toLowerCase()}:{" "}
                <strong>{areaDisplayNames}</strong>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                color="primary"
                size="sm"
                variant="flat"
                onPress={() => {
                  setShowNotification(false);
                  void EventPublisher.publishEvent(
                    EventKeys.AUTO_AREA_FILTER_DISMISS,
                    { org: orgSlug },
                  );
                }}
              >
                Dismiss
              </Button>
              <Button
                color="danger"
                size="sm"
                variant="flat"
                onPress={clearPreferredAreas}
              >
                Clear Preferred {terminology.area.plural}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  return null;
}
